import { useEffect, useState } from 'react';
import { EnvStrip } from './EnvStrip';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  Vote,
  Users,
  BarChart3,
  MapPin,
  LogOut,
  ChevronRight,
  Inbox,
  Newspaper,
  ShieldCheck,
  LayoutDashboard,
  ExternalLink,
  MessageSquare,
  KeyRound,
  Menu,
  X,
  CalendarClock,
  Archive,
  Binoculars,
  Bug,
  Activity,
  KanbanSquare,
  ClipboardCheck,
  BarChart2,
  HeartPulse,
  Rocket,
  ListChecks,
  UserCog,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { EnvProvider, useEnv, ENV_LABELS, ENVS, type Env } from '../lib/env';
import { getClient, getProjectInfo, isConfigured, envVarSuffix, toneOf, type Tone } from '../lib/supabase';
import { start as startSyncWorker } from '../lib/writeQueue/sync';
import { install as installBeforeUnload } from '../lib/writeQueue/beforeunload';
import { QueueStatusIndicator } from './QueueStatusIndicator';
import { MfaGate } from './MfaGate';
import { ScopeProvider, SCOPED_PAGES } from '../lib/scope';
import { ScopePicker, ScopeNarrow } from './ScopePicker';
import { GapAlarm } from './GapAlarm';

// ── Nav structure ─────────────────────────────────────────────────────────────

const PRIMARY_QUEUES = [
  { label: 'Dashboard',      path: 'dashboard',      icon: LayoutDashboard },
  { label: 'System Health',  path: 'system-health',  icon: HeartPulse     },
  { label: 'Curation Reporting', path: 'curation-reporting', icon: BarChart2 },
  { label: 'Schedule',        path: 'schedule',        icon: CalendarClock  },
  { label: 'Triage',         path: 'pipeline-triage', icon: ClipboardCheck },
  { label: 'Suggested Votes', path: 'suggested-votes', icon: FileText       },
  // Themes (founder 2026-10-27: "change admin label to themes and move up into votes area … next to suggested"):
  // theme votes are proposed and green-lit there before they are drafted into Sourced Votes. Was Topics, under Tools.
  { label: 'Themes',          path: 'themes',          icon: Binoculars     },
  { label: 'Sourced Votes',  path: 'sourced-votes',  icon: Newspaper       },
  { label: 'Upcoming Votes', path: 'upcoming-votes', icon: CalendarClock   },
  { label: 'Open Votes',     path: 'open-votes',     icon: Vote            },
  { label: 'Closed Votes',   path: 'closed-votes',   icon: Archive         },
  { label: 'Pre-launch Samples', path: 'sample-votes', icon: Archive       },
  { label: 'Profiles',       path: 'profiles',       icon: Users           },
] as const;

const TOOLS = [
  { label: 'Task Tracker',  path: 'backlog',       icon: KanbanSquare   },
  // Go-Live Tracker retired from the nav 2026-09-30: its 321 items were folded into the Task Tracker
  // (docs/ops/go-live/golive-to-board-2026-09-30.md). Route and page kept ("retire, not kill").
  { label: 'Account Ladder', path: 'account-ladder', icon: ListChecks },
  { label: 'Analytics',     path: 'analytics',     icon: BarChart3      },
  { label: 'Jurisdictions', path: 'jurisdictions', icon: MapPin         },
  { label: 'Source Pool',   path: 'source-trust',  icon: ShieldCheck    },
  { label: 'Admin Errors',  path: 'admin-errors',  icon: Bug            },
  { label: 'Promoter Runs', path: 'promoter-runs', icon: Activity       },
] as const;

const DISTRIBUTION = [
  // Invites + Invite Admin retired 2026-09-24 with the pilot invite tooling (worklist F-13):
  // the invite list was readable with Proto's public key, and Invite Admin carried a shared
  // secret compiled into this bundle.
  { label: 'Feedback',      path: 'tester-feedback',  icon: MessageSquare },
  { label: 'Security',      path: 'security',         icon: KeyRound      },
  { label: 'Personas',      path: 'personas',         icon: UserCog       },
] as const;

// ── Env config ────────────────────────────────────────────────────────────────

// Each tab's label and colour follow the project ref its client connects to, not the env key.
// Red for Prod, green for dev-stage, amber for anything else (founder 2026-10-26; matches EnvStrip).
type EnvLook = { label: string; color: string; badge: string; dot: string; active: string };

const TONE_LOOK: Record<Tone, Omit<EnvLook, 'label'>> = {
  prod:     { color: 'text-red-700',   badge: 'bg-red-50 text-red-700',     dot: 'bg-red-500',   active: 'bg-red-50 text-red-700 font-medium' },
  devstage: { color: 'text-green-700', badge: 'bg-green-50 text-green-700', dot: 'bg-green-500', active: 'bg-green-50 text-green-700 font-medium' },
  unknown:  { color: 'text-amber-700', badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', active: 'bg-amber-50 text-amber-700 font-medium' },
};

function lookFor(e: Env): EnvLook {
  const info = getProjectInfo(e);
  return { label: info.configured ? info.name : ENV_LABELS[e], ...TONE_LOOK[toneOf(info.ref)] };
}

const ENV_CONFIG: Record<Env, EnvLook> = {
  stage:    lookFor('stage'),
  devstage: lookFor('devstage'),
};

// Pages pinned to Prod's client: the company task board, the go-live/ladder trackers, Admin Errors and
// Promoter Runs read getClient('stage') / adminDb('stage') directly. On the dev-stage tab they would show
// Prod's data under a green strip, so dev-stage has no menu entry and no route for them (2026-10-27).
const PROD_ONLY_PATHS: ReadonlySet<string> = new Set([
  'backlog', 'account-ladder', 'golive', 'admin-errors', 'promoter-runs',
]);

function navFor<T extends { path: string }>(env: Env, items: readonly T[]): T[] {
  return env === 'stage' ? [...items] : items.filter(i => !PROD_ONLY_PATHS.has(i.path));
}

/**
 * The environment tab strip — the top of the menu, above every queue.
 *
 * Replaces the previous stacked layout (Prototype's whole nav, then optionally
 * Live's whole nav underneath it). Stacking was fine while the second env was
 * flagged off and never rendered; with two live envs it would have meant two
 * identical 20-item lists in one scroll, where the only thing distinguishing a
 * misclick was a heading the operator had already scrolled past.
 *
 * A tab makes the current project a mode rather than a location, which matches
 * what it actually is: switching env switches the database AND the session.
 * Each tab names its project ref so the choice is never made on a label alone.
 */
function EnvTabs({ env, onNavClick }: { env: Env; onNavClick?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Carry the operator to the SAME page in the other env rather than dumping
  // them on a dashboard: comparing one surface across two projects is the whole
  // reason both are reachable at once.
  const currentPage = (() => {
    const m = /^\/(?:prototype|stage|devstage)\/(.+)$/.exec(location.pathname);
    return m ? m[1] : 'dashboard';
  })();

  return (
    <div className="px-3 pt-3 pb-1">
      <div className="flex gap-1 p-0.5 bg-surface-light rounded-lg border border-border">
        {ENVS.map(e => {
          const cfg = ENV_CONFIG[e];
          const info = getProjectInfo(e);
          const active = e === env;
          const reachable = isConfigured(e);
          return (
            <button
              key={e}
              type="button"
              disabled={!reachable}
              title={
                reachable
                  ? `${info.name} · ${info.ref}`
                  : `Not configured in this build — VITE_SUPABASE_URL_${envVarSuffix(e)} is missing`
              }
              onClick={() => {
                // A Prod-only page has no dev-stage twin; land on the dev-stage dashboard instead.
                const page = e !== 'stage' && PROD_ONLY_PATHS.has(currentPage.split(/[/?]/)[0]) ? 'dashboard' : currentPage;
                navigate(`/${e}/${page}`);
                onNavClick?.();
              }}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                active
                  ? `bg-white shadow-sm ${cfg.color}`
                  : reachable
                    ? 'text-gray-text hover:text-near-black'
                    : 'text-gray-text opacity-40 cursor-not-allowed'
              }`}
            >
              {cfg.label}
              {!reachable && <span className="ml-1 opacity-70">—</span>}
            </button>
          );
        })}
      </div>
      {/* The ref, always. A tab label is a name someone chose; the ref is what
          the build is actually pointed at. */}
      <p className="mt-1 px-1 text-[10px] font-mono text-gray-text truncate">
        {getProjectInfo(env).ref ?? 'NOT CONFIGURED'}
      </p>
    </div>
  );
}

function getInitials(name: string) {
  return name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
}

// ── Env section ───────────────────────────────────────────────────────────────

function EnvSection({ env, onNavClick }: { env: Env; onNavClick?: () => void }) {
  const cfg = ENV_CONFIG[env];

  return (
    <div className="mb-1">
      <div className="px-4 pt-4 pb-1 flex items-center gap-2">
        <span className={`text-xs font-bold uppercase tracking-wider ${cfg.color}`}>
          {cfg.label}
        </span>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} inline-block animate-pulse`} />
      </div>

      <div className="mb-1">
        <div className="px-4 py-0.5 text-xs font-medium text-gray-text uppercase tracking-wider opacity-60">
          Queues
        </div>
        {navFor(env, PRIMARY_QUEUES).map(item => (
          <NavLink
            key={`${env}-${item.path}`}
            to={`/${env}/${item.path}`}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                isActive
                  ? cfg.active
                  : 'text-near-black hover:bg-surface-light'
              }`
            }
          >
            <item.icon size={15} />
            {item.label}
          </NavLink>
        ))}
      </div>

      <div className="mb-1">
        <div className="px-4 py-0.5 text-xs font-medium text-gray-text uppercase tracking-wider opacity-60">
          Tools
        </div>
        {navFor(env, TOOLS).map(item => (
          <NavLink
            key={`${env}-${item.path}`}
            to={`/${env}/${item.path}`}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                isActive
                  ? cfg.active
                  : 'text-near-black hover:bg-surface-light'
              }`
            }
          >
            <item.icon size={15} />
            {item.label}
          </NavLink>
        ))}
      </div>

      <div className="mb-1">
        <div className="px-4 py-0.5 text-xs font-medium text-gray-text uppercase tracking-wider opacity-60">
          Distribution
        </div>
        {navFor(env, DISTRIBUTION).map(item => (
          <NavLink
            key={`${env}-${item.path}`}
            to={`/${env}/${item.path}`}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                isActive
                  ? cfg.active
                  : 'text-near-black hover:bg-surface-light'
              }`
            }
          >
            <item.icon size={15} />
            {item.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

// ── Top bar ───────────────────────────────────────────────────────────────────

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const env = useEnv();
  const location = useLocation();
  const cfg = ENV_CONFIG[env];

  const segment = location.pathname.split('/').pop() ?? '';
  const PAGE_LABELS: Record<string, string> = {
    dashboard:         'Dashboard',
    'system-health':   'System Health',
    'curation-reporting': 'Curation Reporting',
    'suggested-votes': 'Suggested Votes',
    'sourced-votes':   'Sourced Votes',
    'upcoming-votes':  'Upcoming Votes',
    'open-votes':      'Open Votes',
    'closed-votes':    'Closed Votes',
    'sample-votes':    'Pre-launch Samples',
    profiles:          'Profiles',
    analytics:         'Analytics',
    jurisdictions:     'Jurisdictions',
    'source-trust':    'Source Pool',
    'themes':          'Themes',
    'topics':          'Themes',
    'tester-feedback': 'Feedback',
    'my-queue':        'My Queue',
    'schedule':        'Schedule Dashboard',
    // Added 2026-09-20 with the go-live tracker. The five below were missing, so
    // `PAGE_LABELS[segment] ?? 'Dashboard'` made each of those pages claim to be the
    // Dashboard in the top bar.
    'golive':          'Go-Live Tracker',
    'account-ladder':  'Account Ladder',
    'backlog':         'Task Tracker',
    'admin-errors':    'Admin Errors',
    'pipeline-triage': 'Triage',
    'promoter-runs':   'Promoter Runs',
    'security':        'Security',
    'personas':        'Personas',
  };
  const pageTitle = PAGE_LABELS[segment] ?? 'Dashboard';
  const isPersonal = location.pathname === '/my-queue';

  return (
    <header className="h-14 bg-white border-b border-border flex items-center px-4 gap-2 flex-shrink-0">
      {/* Hamburger — mobile only */}
      <button
        onClick={onMenuClick}
        className="md:hidden p-1.5 -ml-1 text-gray-text hover:text-near-black transition-colors"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {isPersonal ? (
        <span className="text-sm font-semibold text-near-black">My Queue</span>
      ) : (
        <>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.badge}`}>
            {cfg.label}
          </span>
          <ChevronRight size={12} className="text-gray-text" />
          <span className="text-sm font-semibold text-near-black truncate">{pageTitle}</span>
          {/* The scope (spec admin-scope-and-gaps-spec.md): editor pages only; hidden where the database has no scope yet. */}
          {SCOPED_PAGES.has(segment) && <span className="hidden md:flex items-center gap-2"><ChevronRight size={12} className="text-gray-text shrink-0" /><ScopePicker /></span>}
        </>
      )}

      {/* Write-queue chrome indicator (Item #3 MVP, 2026-05-19). Right-
       *  aligned via ml-auto. Plan reference:
       *  docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 3. */}
      <div className="ml-auto">
        <QueueStatusIndicator />
      </div>
    </header>
  );
}

// ── Sidebar content ───────────────────────────────────────────────────────────

function SidebarContent({ onNavClick, onClose }: { onNavClick?: () => void; onClose?: () => void }) {
  const { user, logout } = useAuth();
  const env = useEnv();

  return (
    <div className="w-64 md:w-56 border-r border-border bg-white flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 py-3.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-brand-orange flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">V</span>
          </div>
          <h1 className="text-base font-bold text-near-black tracking-tight">VOTD Admin</h1>
        </div>
        {/* Close button — mobile only */}
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden p-1 text-gray-text hover:text-near-black transition-colors"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2">
        <div className="mb-1">
          <div className="px-4 pt-3 pb-1">
            <span className="text-xs font-bold uppercase tracking-wider text-near-black opacity-50">
              Personal
            </span>
          </div>
          <NavLink
            to="/my-queue"
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                isActive
                  ? 'bg-brand-orange-tint text-brand-orange font-medium'
                  : 'text-near-black hover:bg-surface-light'
              }`
            }
          >
            <Inbox size={15} />
            My Queue
          </NavLink>
        </div>

        <div className="mx-4 my-2 border-t border-border" />
        <EnvTabs env={env} onNavClick={onNavClick} />
        <EnvSection env={env} onNavClick={onNavClick} />
      </nav>

      {/* External links */}
      <div className="border-t border-border px-4 py-2">
        <a
          href="https://docs.votd.io"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-0 py-1.5 text-sm text-near-black hover:text-brand-orange transition-colors"
        >
          <ExternalLink size={15} />
          Documentation
        </a>
      </div>

      {/* User footer */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-2.5 px-1 py-1">
          <div className="w-7 h-7 rounded-full bg-brand-orange flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-semibold">
              {user ? getInitials(user.name) : '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-near-black truncate">{user?.name ?? 'Admin'}</p>
            <p className="text-xs text-gray-text truncate">{user?.email ?? ''}</p>
          </div>
          <button
            onClick={logout}
            title="Sign out"
            className="text-gray-text hover:text-near-black transition-colors"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

function LayoutInner() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const env = useEnv();

  // Write-queue bootstrap (2026-05-19 — fragility rebuild Item #1).
  //
  // Starts the sync worker so every editor-driven mutation routed through
  // the writeQueue (executeHold / executeApprove / performReject /
  // handleRateSource / handleEditField in EditorialQueue.tsx) gets drained
  // against Supabase with exponential-backoff retry and zombie recovery on
  // restart. Installs the beforeunload guard so closing the tab with
  // pending mutations triggers the browser's "Leave site?" prompt.
  //
  // Env dependency: on env switch the worker restarts so it dispatches
  // against the right Supabase client. (For LIVE_ENV_ENABLED=false today,
  // env is always 'prototype' so this only fires once on mount.)
  //
  // Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1.
  useEffect(() => {
    const worker = startSyncWorker(() => getClient(env));
    const uninstall = installBeforeUnload();
    return () => {
      worker.stop();
      uninstall();
    };
  }, [env]);

  return (
    <div className="flex flex-col h-screen">
    {/* Which database this admin is connected to — red PROD, green DEV-STAGE (founder 2026-10-26). */}
    <EnvStrip />
    <div className="flex flex-1 min-h-0 bg-surface-light overflow-hidden">

      {/* Mobile sidebar overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar — fixed overlay on mobile, static on desktop */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-30 flex flex-col transition-transform duration-200
          md:static md:translate-x-0 md:flex md:z-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent onNavClick={closeSidebar} onClose={closeSidebar} />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          {SCOPED_PAGES.has(location.pathname.split('/')[2] ?? '') && <>
            {/* Phones: the picker gets its own row under the top bar. */}
            <div className="md:hidden px-4 pt-3"><ScopePicker /></div>
            <GapAlarm />
            <ScopeNarrow />
          </>}
          <Outlet />
        </main>
      </div>
    </div>
    </div>
  );
}

/** One scope per env tab, shared by the picker, the gap alarm and every editor page. */
function ScopedShell() {
  const env = useEnv();
  return <ScopeProvider key={env} env={env}><LayoutInner /></ScopeProvider>;
}

export function AdminLayout() {
  return (
    <EnvProvider>
      {/* MfaGate wraps the WHOLE authenticated shell, not a route. An admin with a verified
          factor at aal1 cannot read any of the 23 gated tables, so every queue would render
          empty with no explanation — gating one page would leave the rest silently broken.
          It renders children untouched when there is nothing to challenge. */}
      <MfaGate>
        <ScopedShell />
      </MfaGate>
    </EnvProvider>
  );
}
